import { useEffect, useState } from "react";

interface HeroSectionProps {
  onNavigate: (id: string) => void;
}

export default function HeroSection({ onNavigate }: HeroSectionProps) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      id="hero"
      className="relative w-full h-screen min-h-[600px] overflow-hidden flex items-center"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/edited_a81341c89a86412bd3cb16aef1663007_43698a8a.jpg"
          alt="Rustic Copper luxury jewelry editorial"
          className="w-full h-full object-cover object-center"
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/35 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-black/10" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 md:px-10 w-full">
        <div
          className={`max-w-xl transition-all duration-1200 ${
            loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
          style={{ transitionDelay: "200ms" }}
        >
          {/* Logo */}
          <img
            src="https://static.readdy.ai/image/61892072331720d5ae52870a50f3ccd8/5abd40eda45740105ef90e7db4972552.png"
            alt="Rustic Copper"
            className="h-12 sm:h-16 md:h-20 w-auto mb-6 md:mb-10 brightness-0 invert opacity-90"
          />

          {/* Eyebrow */}
          <p
            className="text-[#D4AF87] text-[10px] uppercase tracking-[3px] md:tracking-[4px] mb-4 md:mb-6"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Handcrafted — Since 2018
          </p>

          {/* Headline */}
          <h1
            className="text-[#FAF8F3] text-4xl sm:text-5xl md:text-6xl leading-tight mb-4 md:mb-6"
            style={{ fontFamily: "Cinzel, serif", fontWeight: 400 }}
          >
            Handcrafted
            <br />
            Copper Jewelry
          </h1>

          {/* Subheadline */}
          <p
            className="text-[#FAF8F3]/80 text-base sm:text-xl italic mb-8 md:mb-10 leading-relaxed"
            style={{ fontFamily: "Playfair Display, serif", fontWeight: 400 }}
          >
            Jewelry designed to be felt, not just worn.
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-5">
            <button
              onClick={() => onNavigate("collection")}
              className="group text-xs uppercase tracking-[2px] px-8 sm:px-10 py-3 sm:py-4 bg-[#D4AF87] text-[#3D2817] hover:bg-[#C9A07A] transition-all duration-400 cursor-pointer whitespace-nowrap w-full sm:w-auto text-center"
              style={{ fontFamily: "Inter, sans-serif", borderRadius: "2px" }}
            >
              Explore Collection
            </button>
            <button
              onClick={() => onNavigate("editorial")}
              className="text-xs uppercase tracking-[2px] text-[#D4AF87] border border-[#D4AF87]/50 px-8 sm:px-10 py-3 sm:py-4 hover:border-[#D4AF87] hover:bg-white/5 transition-all duration-400 cursor-pointer whitespace-nowrap w-full sm:w-auto text-center"
              style={{ fontFamily: "Inter, sans-serif", borderRadius: "2px" }}
            >
              Our Story
            </button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 md:bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span
          className="text-[#D4AF87]/60 text-[10px] uppercase tracking-[3px]"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          Scroll
        </span>
        <div className="w-px h-6 md:h-8 bg-gradient-to-b from-[#D4AF87]/60 to-transparent" />
      </div>
    </section>
  );
}
